// Turns a local analytics-engine result into a plain-text description for
// Groq to phrase naturally. Groq only ever sees this small summary — never
// the dataset or the raw result object.
//
// CRITICAL: the analytics engine is authoritative. Every value below has
// ALREADY had its aggregation applied — if this says AVERAGE, the numbers
// ARE averages; if it says SUM, the numbers ARE totals. This formatter's
// entire job is to make that fact impossible to miss or misread, so the
// text that reaches Groq never leaves room for it to reinterpret,
// recalculate, or guess which operation produced these numbers.

const AGGREGATION_LABELS = {
  sum: "SUM (total)",
  average: "AVERAGE (mean)",
  count: "COUNT (number of records)",
  maximum: "MAX (maximum)",
  minimum: "MIN (minimum)",
  median: "MEDIAN",
  mode: "MODE (most frequent value)",
};

function aggLabel(agg) {
  return AGGREGATION_LABELS[agg] || (agg ? agg.toUpperCase() : "UNKNOWN");
}

function fmtNum(n) {
  if (n === null || n === undefined || isNaN(n)) return "N/A";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function filtersLine(appliedFilters) {

  if (!appliedFilters?.length) return "Filters: none";

  const parts = appliedFilters.map(f => {
    const label = f.datePart ? `${f.datePart} of ${f.column}` : f.column;
    const op = f.operator === "=" ? "=" : f.operator;
    return `${label} ${op} ${f.value}`;
  });

  return `Filters: ${parts.join(", ")} (ALREADY applied to every value below — these are not unfiltered/overall numbers)`;
}

export function formatToolResult(result) {

  if (!result?.success) {
    return result?.error ? `Could not compute this: ${result.error}` : null;
  }

  const { tool, measure, value, rows = 0, appliedFilters } = result;
  const filters = filtersLine(appliedFilters);

  switch (tool) {

    // ---- Simple scalar aggregates over the (possibly filtered) dataset ----
    case "average":
    case "sum":
    case "maximum":
    case "minimum":
    case "median":
    case "mode": {

      const resultLine = tool === "mode"
        ? `Result: ${value} (the most frequently occurring value)`
        : `Result: ${fmtNum(value)} (calculated across ${rows.toLocaleString()} records)`;

      return [
        `Measure: ${measure}`,
        `Aggregation: ${aggLabel(tool)}`,
        filters,
        resultLine,
      ].join("\n");
    }

    case "count":
      return [
        `Aggregation: ${aggLabel("count")}`,
        filters,
        `Result: ${rows.toLocaleString()} matching records`,
      ].join("\n");

    // ---- Grouped breakdown (e.g. "average Value by Year") ----
    case "table": {

      const { dimension, measure: groupMeasure, aggregation } = result.appliedGroupBy || {};

      if (!dimension) {
        // A raw (non-grouped) row dump — no single aggregation to label.
        const preview = result.data.map(r => JSON.stringify(r)).join("\n");
        return `Result table (${result.totalRows} rows${result.truncated ? `, showing first ${result.data.length}` : ""}):\n${preview}`;
      }

      const resultsList = result.data
        .map(r => `${r.group}: ${fmtNum(r.value)}`)
        .join("\n");

      return [
        `Measure: ${groupMeasure}`,
        `Grouped by: ${dimension}`,
        `Aggregation: ${aggLabel(aggregation)}`,
        filters,
        `Results (${result.data.length} groups${result.truncated ? `, showing first ${result.data.length} of ${result.totalRows}` : ""}) — these values are ALREADY the ${aggLabel(aggregation).split(" ")[0]} for each group, do not recalculate them:`,
        resultsList,
      ].join("\n");
    }

    // ---- Time-series breakdown ----
    case "trend": {

      const resultsList = result.series
        .map(p => `${p.period}: ${fmtNum(p.value)}`)
        .join("\n");

      const overall = result.direction === "insufficient-data"
        ? "Not enough periods to determine an overall direction."
        : `Overall: ${result.direction}` + (result.pctChange !== null ? ` (${fmtNum(result.pctChange)}% change from first to last period)` : "");

      return [
        `Measure: ${result.measure}`,
        `Grouped by: ${result.period} (time period)`,
        `Aggregation: ${aggLabel(result.aggregation)}`,
        filters,
        `Results (${result.series.length} periods) — these values are ALREADY the ${aggLabel(result.aggregation).split(" ")[0]} for each period, do not recalculate them:`,
        resultsList,
        overall,
      ].join("\n");
    }

    // ---- Two-way comparison ----
    case "compare": {

      return [
        `Measure: ${result.measure}`,
        `Aggregation: ${aggLabel(result.aggregation)}`,
        `Comparing: ${result.column} = ${result.a.label}  vs  ${result.column} = ${result.b.label}`,
        filters,
        `Results — these are ALREADY the ${aggLabel(result.aggregation).split(" ")[0]} for each side, do not recalculate them:`,
        `${result.a.label}: ${fmtNum(result.a.value)} (${result.a.rows} records)`,
        `${result.b.label}: ${fmtNum(result.b.value)} (${result.b.rows} records)`,
        `Change: ${fmtNum(result.change)}` + (result.pctChange !== null ? ` (${fmtNum(result.pctChange)}%)` : ""),
      ].join("\n");
    }

    case "correlation":
      return [
        `Correlation between ${result.columnX} and ${result.columnY} is ${result.coefficient} — a ${result.strength} ${result.direction} relationship (n=${result.sampleSize}).`,
        filters,
        "This is a correlation coefficient, not an aggregation — do not describe it as a sum, average, or count.",
      ].join("\n");

    case "chart": {

      const agg = result.aggregation ? aggLabel(result.aggregation) : null;
      const axis = result.dateColumn
        ? `${result.measure} by ${result.period}`
        : `${result.measure} by ${result.dimension}`;

      return [
        `Generated a ${result.chartType} chart of: ${axis}`,
        agg ? `Aggregation: ${agg}` : null,
        filters,
        `The chart already shows ${result.data.length} ${result.dateColumn ? "time points" : "categories"} — do not recalculate or reinterpret these values.`,
      ].filter(Boolean).join("\n");
    }

    case "driver": {

      const verbed = result.direction === "increased" ? "increased" : result.direction === "decreased" ? "decreased" : "remained stable";
      const changeText = result.direction === "stable" ? "" : ` (${fmtNum(Math.abs(result.totalPctChange))}%)`;

      let out = `Measure: ${result.measure}\nAggregation: SUM (total per period)\n${filters}\n`;
      out += `FACT: total ${result.measure} ${verbed}${changeText} from ${result.periodA} to ${result.periodB} (${fmtNum(result.totalA)} to ${fmtNum(result.totalB)}).\n`;

      if (result.countChangePct !== null && result.avgChangePct !== null) {
        out += `SUPPORTING EVIDENCE: over the same period, transaction count changed ${fmtNum(result.countChangePct)}% and average transaction value changed ${fmtNum(result.avgChangePct)}%.\n`;
      }

      if (result.dimensionContributions?.length) {

        out += "POSSIBLE CONTRIBUTING FACTORS (largest observed movers, ranked by share of the total change):\n";

        result.dimensionContributions.forEach(dc => {
          const top = dc.topContributors[0];
          if (top) {
            out += `- ${dc.dimension}: "${top.label}" changed by ${fmtNum(top.change)} (${top.shareOfTotalChange !== null ? fmtNum(top.shareOfTotalChange) + "% of total change" : "share unknown"}).\n`;
          }
        });
      }

      out += "IMPORTANT: this is observational association, not a confirmed cause. The available dimensions do not establish a definitive causal relationship — do not state that any factor 'caused' the change, only that it is 'associated with' or 'the largest observed contributor to' it.";

      return out;
    }

    default:
      return null;

  }

}
